import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, ChevronRight, SlidersHorizontal, Info, Plus, X } from 'lucide-react'
import { inputCls } from '../styles'
import { DEFAULT_DUPLICATE_RULES } from '../data'
import type { DuplicateMatchField, DuplicateMatchMethod, DuplicateMatchStrength, DuplicateRuleConfig } from '../types'
import { useAiSeed } from '../aiConfigure'
import type { AiSeed } from '../aiConfigure'

const ALL_MATCH_FIELDS: DuplicateMatchField[] = [
  'EXTERNAL_REFERENCE', 'DOC_PRIMARY_IDENTIFIER', 'DOC_SECONDARY_IDENTIFIER', 'DOC_COUNTRY', 'DOC_SUBDIVISION', 'DOC_TYPE',
  'PHONE_NUMBER', 'EMAIL_ADDRESS', 'GIVEN_NAME', 'FAMILY_NAME', 'DATE_OF_BIRTH', 'ADDR_NORM_SHORT', 'ADDR_NORM_LONG',
]

const STRENGTH_STYLE: Record<DuplicateMatchStrength, { bg: string; color: string }> = {
  LOW: { bg: 'rgba(148,163,184,.15)', color: '#475569' },
  MEDIUM: { bg: 'rgba(251,146,60,.15)', color: '#c2410c' },
  HIGH: { bg: 'rgba(129,140,248,.15)', color: '#4f46e5' },
  VERY_HIGH: { bg: 'rgba(248,113,113,.15)', color: '#b91c1c' },
}

function StrengthBadge({ strength }: { strength: DuplicateMatchStrength }) {
  const style = STRENGTH_STYLE[strength]
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: style.bg, color: style.color }}>{strength.replace('_', ' ')}</span>
}

function RuleFields({ fields }: { fields: DuplicateMatchField[] }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {fields.map((f) => (
        <span key={f} className="text-[9px] px-1.5 py-0.5 rounded bg-[#e5e7eb] text-[#64748b] font-mono">{f}</span>
      ))}
    </div>
  )
}

function SubSection({ title, description, children, defaultOpen = false }: { title: string; description: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        {open ? <ChevronDown size={12} className="text-[#64748b] shrink-0" /> : <ChevronRight size={12} className="text-[#64748b] shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-[#1e293b]">{title}</p>
          <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{description}</p>
        </div>
      </button>
      {open && <div className="p-3 space-y-2" style={{ borderTop: '1px solid #e5e7eb' }}>{children}</div>}
    </div>
  )
}

interface DuplicatesConfigPanelProps {
  onRulesChange: (rules: { defaultRules: DuplicateRuleConfig[]; customRules: DuplicateRuleConfig[] }) => void
  /** AI Configure from Scope — applied once the rep accepts the suggestion. */
  aiSeed?: AiSeed<DuplicateRuleConfig[]> | null
}

export function DuplicatesConfigPanel({ onRulesChange, aiSeed }: DuplicatesConfigPanelProps) {
  const [open, setOpen] = useState(true)
  const [customRules, setCustomRules] = useState<DuplicateRuleConfig[]>([])

  useAiSeed(aiSeed, setCustomRules)
  const [newName, setNewName] = useState('')
  const [newFields, setNewFields] = useState<DuplicateMatchField[]>([])
  const [newMethod, setNewMethod] = useState<DuplicateMatchMethod>('FUZZY')
  const [newStrength, setNewStrength] = useState<DuplicateMatchStrength>('MEDIUM')

  useEffect(() => {
    onRulesChange({ defaultRules: DEFAULT_DUPLICATE_RULES, customRules })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customRules])

  function toggleField(field: DuplicateMatchField) {
    setNewFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]))
  }

  function addRule() {
    if (!newName.trim() || newFields.length === 0) return
    setCustomRules((prev) => [
      ...prev,
      { id: `custom-${prev.length}-${newName.trim().toLowerCase().replace(/\s+/g, '-')}`, name: newName.trim(), matchFields: newFields, method: newMethod, strength: newStrength, custom: true },
    ])
    setNewName('')
    setNewFields([])
    setNewMethod('FUZZY')
    setNewStrength('MEDIUM')
  }

  function removeRule(id: string) {
    setCustomRules((prev) => prev.filter((r) => r.id !== id))
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          Duplicate Detection Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[10px] text-[#475569] flex items-start gap-1.5">
            <Info size={11} className="shrink-0 mt-0.5" />
            Modelled on Operations Center&apos;s Duplicates tab — these match rules compare a new applicant against previously verified entities. Default rules always run; custom rules can be layered on top for this customer.
          </p>

          <SubSection title="Default Rules" description={`${DEFAULT_DUPLICATE_RULES.length} platform rules, always active for every customer`} defaultOpen>
            <div className="space-y-1.5">
              {DEFAULT_DUPLICATE_RULES.map((rule) => (
                <div key={rule.id} className="px-3 py-2 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-[#1e293b]">{rule.name}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[9px] font-mono text-[#64748b]">{rule.method}</span>
                      <StrengthBadge strength={rule.strength} />
                    </div>
                  </div>
                  <RuleFields fields={rule.matchFields} />
                </div>
              ))}
            </div>
          </SubSection>

          <SubSection title="Custom Rules" description={`${customRules.length} custom rule${customRules.length === 1 ? '' : 's'} for this customer`}>
            <div className="space-y-1.5">
              {customRules.length === 0 && (
                <p className="text-[10px] text-[#475569]">No custom rules configured.</p>
              )}
              {customRules.map((rule) => (
                <div key={rule.id} className="px-3 py-2 rounded-[9px] flex items-start justify-between gap-2" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-[#1e293b]">{rule.name}</span>
                      <span className="text-[9px] font-mono text-[#64748b]">{rule.method}</span>
                      <StrengthBadge strength={rule.strength} />
                    </div>
                    <RuleFields fields={rule.matchFields} />
                  </div>
                  <button onClick={() => removeRule(rule.id)} className="text-[#64748b] hover:text-red-700 shrink-0">
                    <X size={13} />
                  </button>
                </div>
              ))}

              <div className="p-3 rounded-[9px] space-y-2" style={{ background: '#f8fafc', border: '1px dashed #e5e7eb' }}>
                <input className={inputCls} placeholder="Rule name…" value={newName} onChange={(e) => setNewName(e.target.value)} />
                <div className="flex flex-wrap gap-1.5">
                  {ALL_MATCH_FIELDS.map((field) => {
                    const active = newFields.includes(field)
                    return (
                      <button
                        key={field}
                        onClick={() => toggleField(field)}
                        className="text-[9px] px-1.5 py-0.5 rounded font-mono transition-colors"
                        style={active
                          ? { background: '#4f46e5', color: '#ffffff', border: '1px solid rgba(99,102,241,.4)' }
                          : { background: '#ffffff', color: '#64748b', border: '1px solid #e5e7eb' }
                        }
                      >
                        {field}
                      </button>
                    )
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <select className={inputCls} style={{ width: 100 }} value={newMethod} onChange={(e) => setNewMethod(e.target.value as DuplicateMatchMethod)}>
                    <option value="EXACT">EXACT</option>
                    <option value="FUZZY">FUZZY</option>
                  </select>
                  <select className={inputCls} style={{ width: 120 }} value={newStrength} onChange={(e) => setNewStrength(e.target.value as DuplicateMatchStrength)}>
                    <option value="LOW">LOW</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="HIGH">HIGH</option>
                    <option value="VERY_HIGH">VERY_HIGH</option>
                  </select>
                  <button
                    onClick={addRule}
                    disabled={!newName.trim() || newFields.length === 0}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-[7px] text-[11px] font-medium text-white disabled:opacity-40 shrink-0"
                    style={{ background: '#6366f1' }}
                  >
                    <Plus size={12} /> Add
                  </button>
                </div>
              </div>
            </div>
          </SubSection>
        </div>
      )}
    </div>
  )
}
